import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart3, ChevronRight, CircleDollarSign, ClipboardList, Droplets, Package, Users, Wheat, Beef, Milk, Sprout, Plus, TriangleAlert, Receipt, CloudRain } from "lucide-react";
import { api, money, num } from "../lib/api";
import { PageHeader } from "../components/farm/FarmUI";
import { HERO, HERO_TINT } from "../lib/heroes";

const ALERT_ICON = { cheque: Receipt, stock: Package, salary: Users, harvest: Wheat, weather: CloudRain };

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState({ items: [], counts: { total: 0, high: 0 } });
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([api.get("/summary"), api.get("/alerts")])
      .then(([s, a]) => { setSummary(s); setAlerts(a); })
      .catch(() => setError("Özet bilgi yüklenemedi."));
  }, []);

  const t = summary?.totals || { income: 0, expense: 0, net: 0 };
  const empty = !summary || (t.income === 0 && t.expense === 0 && (summary?.rice?.field_count || 0) === 0);
  const topAlerts = alerts.items.slice(0, 4);

  return (
    <>
      <PageHeader
        eyebrow="KEMAL NAMLI ÇİFTLİĞİ · 2026"
        title="Günaydın, Faik Bey"
        subtitle="Çiftliğinizin bugünkü durumu ve son hareketler burada."
        heroImage={HERO.dashboard}
        heroTint={HERO_TINT.dashboard}
        actions={
          <Link to="/reports" className="outline-btn" data-testid="dashboard-reports-link">
            <BarChart3 size={17} /> Raporlar
          </Link>
        }
      />

      {error && <div className="api-error" data-testid="api-error">{error}</div>}

      <section className="stats-grid">
        <Stat title="Toplam gelir" value={money(t.income)} trend="Tüm hareketler" tone="green" icon={CircleDollarSign} testId="stat-income" />
        <Stat title="Toplam gider" value={money(t.expense)} trend="Tüm hareketler" tone="orange" icon={ClipboardList} testId="stat-expense" />
        <Stat title="Net durum" value={money(t.net)} trend={t.net >= 0 ? "Kâr" : "Zarar"} tone="teal" icon={BarChart3} testId="stat-net" />
        <Stat title="Düşük stok" value={`${summary?.stock?.low_stock_count || 0} kalem`} trend={alerts.counts.high ? `${alerts.counts.high} acil uyarı` : "Sorun yok"} tone="red" icon={Package} testId="stat-lowstock" />
      </section>

      <section className="dashboard-grid">
        <div className="panel">
          <div className="panel-head">
            <div>
              <h2>Modül özetleri</h2>
              <p>Her bölümdeki güncel toplamlar</p>
            </div>
          </div>
          <div className="module-grid">
            <ModuleCard to="/rice" icon={Wheat} label="Çeltik" value={`${num(summary?.rice?.total_donum || 0)} dönüm`} sub={`${num(summary?.rice?.total_yield_kg || 0)} kg · ${summary?.rice?.field_count || 0} tarla`} tone="green" testId="module-rice" />
            <ModuleCard to="/dairy" icon={Droplets} label="Süt" value={`${num(summary?.dairy?.total_liters || 0)} lt`} sub={`${summary?.dairy?.record_count || 0} günlük kayıt`} tone="teal" testId="module-dairy" />
            <ModuleCard to="/cattle" icon={Milk} label="Büyükbaş" value={`${summary?.cattle?.alive || 0} hayvan`} sub={`${summary?.cattle?.calves || 0} buzağı · ${summary?.cattle?.sold || 0} satılan`} tone="teal" testId="module-cattle" />
            <ModuleCard to="/sheep" icon={Beef} label="Küçükbaş" value={`${summary?.sheep?.animal_count || 0} hayvan`} sub={`${summary?.sheep?.record_count || 0} hareket`} tone="orange" testId="module-sheep" />
            <ModuleCard to="/employees" icon={Users} label="Çalışan" value={`${summary?.employees?.count || 0} kişi`} sub={`${summary?.employees?.payment_count || 0} ödeme`} tone="deep" testId="module-employees" />
            <ModuleCard to="/stock" icon={Package} label="Stok" value={`${summary?.stock?.item_count || 0} kalem`} sub={`${summary?.stock?.low_stock_count || 0} düşük`} tone="red" testId="module-stock" />
            <ModuleCard to="/cheques" icon={Receipt} label="Çekler" value={`${alerts.counts.by_type?.cheque || 0} uyarı`} sub="Vade takibi" tone="orange" testId="module-cheques" />
            <ModuleCard to="/finance" icon={CircleDollarSign} label="Kasa" value={money(t.net)} sub="Genel bakiye" tone="green" testId="module-finance" />
          </div>
        </div>

        <div className="panel alerts-panel">
          <div className="panel-head">
            <div>
              <h2>Uyarılar</h2>
              <p>Dikkat gerekenler (birleşik hatırlatmalar)</p>
            </div>
            <span className={`alert-count ${alerts.counts.high ? "hot" : ""}`} data-testid="alerts-count">{alerts.counts.total}</span>
          </div>
          {topAlerts.length === 0 ? (
            <div className="alert-empty">
              <TriangleAlert size={24} />
              <b>Şu an uyarı yok</b>
              <small>Her şey yolunda görünüyor.</small>
            </div>
          ) : (
            topAlerts.map((it) => {
              const Icon = ALERT_ICON[it.type] || TriangleAlert;
              return (
                <Link to={it.link || "/"} className={`alert-item sev-${it.severity}`} key={it.id} data-testid={`dash-alert-${it.id}`}>
                  <div className={`alert-icon sev-${it.severity}`}><Icon size={18} /></div>
                  <div>
                    <b>{it.title}</b>
                    <small>{it.subtitle}</small>
                  </div>
                  <ChevronRight size={17} />
                </Link>
              );
            })
          )}
          {alerts.items.length > 4 && (
            <div className="text-btn" data-testid="dash-alerts-more">
              +{alerts.items.length - 4} uyarı daha · Sağ üst zil simgesi <ChevronRight size={15} />
            </div>
          )}
        </div>
      </section>

      <section className="panel quick-panel" style={{ marginTop: 20 }}>
        <div className="panel-head">
          <div>
            <h2>Hızlı işlemler</h2>
            <p>Sık kullanılan modüller</p>
          </div>
        </div>
        <div className="quick-grid">
          <QuickLink to="/rice" icon={Wheat} label="Çeltik tarlası ekle" testId="quick-rice" />
          <QuickLink to="/dairy" icon={Droplets} label="Günlük süt kaydı" testId="quick-dairy" />
          <QuickLink to="/cattle" icon={Milk} label="Büyükbaş / buzağı ekle" testId="quick-cattle" />
          <QuickLink to="/sheep" icon={Beef} label="Küçükbaş hareketi" testId="quick-sheep" />
          <QuickLink to="/employees" icon={Users} label="Çalışan / maaş" testId="quick-employees" />
          <QuickLink to="/stock" icon={Package} label="Stok girişi" testId="quick-stock" />
          <QuickLink to="/cheques" icon={Receipt} label="Çek ekle" testId="quick-cheques" />
          <QuickLink to="/finance" icon={CircleDollarSign} label="Gelir / gider" testId="quick-finance" />
        </div>
      </section>

      {empty && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="onboarding-hint" data-testid="onboarding-hint">
          <Sprout size={22} />
          <div>
            <b>Çiftliğinize hoş geldiniz</b>
            <small>Sol menüden veya yukarıdaki hızlı işlemlerden başlayın. Verileriniz güvenli şekilde saklanır.</small>
          </div>
        </motion.div>
      )}
    </>
  );
}

function Stat({ title, value, trend, tone, icon: Icon, testId }) {
  return (
    <div className="stat-card" data-testid={testId}>
      <div className={`stat-icon ${tone}`}><Icon size={19} /></div>
      <span>{title}</span>
      <strong>{value}</strong>
      <small className={tone === "red" ? "negative" : "positive"}>
        {tone !== "red" && "↗ "}{trend}
      </small>
    </div>
  );
}

function ModuleCard({ to, icon: Icon, label, value, sub, tone, testId }) {
  return (
    <Link to={to} className={`module-card tone-${tone}`} data-testid={testId}>
      <Icon size={22} />
      <div className="module-body">
        <small>{label}</small>
        <b>{value}</b>
        <span>{sub}</span>
      </div>
      <ChevronRight size={16} className="module-chev" />
    </Link>
  );
}

function QuickLink({ to, icon: Icon, label, testId }) {
  return (
    <Link to={to} className="quick-action" data-testid={testId}>
      <Icon size={20} />
      <span>{label}</span>
      <Plus size={15} />
    </Link>
  );
}
